import React, { useContext } from 'react';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { UserContext } from '@/components/ContextProviders';
import { openLink } from '@/utils';
import { useRouter } from 'next/router';

// Declare HubSpot global
declare global {
  interface Window {
    hbspt: any;
  }
}

type ProfileModelModel = ModuleBaseModel<
  {
    details?: {
      title?: string;
      firstName?: string;
      lastName?: string;
      mailingCountry?: string;
    };
  } & ModuleBasePropsModel
>;

const ProfileModel: React.FC<ProfileModelModel> = (data) => {
  const { props } = data;
  const user = useContext(UserContext);
  const details = props?.details;

  const router = useRouter();

  // Construct full name
  const fullName = details ? `${details.firstName || ''} ${details.lastName || ''}`.trim() : '';
  const jobTitle = details?.title || '';
  const email = user?.email || '';
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);

  // Load HubSpot script
  React.useEffect(() => {
    if (email) {
      addHSScript(email);
    }
  }, [email]);

  const addHSScript = (email: string) => {
    const script = document.createElement('script');
    script.src = 'https://js-eu1.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    script.onload = () => {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: '26632421',
          formId: '66b2e004-efe1-40ad-81b1-2b410e90ae96',
          region: 'eu1',
          target: '#hubspot-form',
          onFormSubmitted: function () {
            setShowSuccessMessage(true);
            setTimeout(() => {
              setShowSuccessMessage(false);
            }, 3000);
          },
          onFormReady: function ($form: any) {
            const interval = setInterval(() => {
              const emailInput = $form.querySelector('input[name="email"]');
              if (emailInput && emailInput.value !== email) {
                emailInput.value = email;
                emailInput.readOnly = true;
              }
            }, 100);

            // Clear interval after 5 seconds to avoid infinite checking
            setTimeout(() => clearInterval(interval), 5000);
          },
        });
      }
    };
    document.body.appendChild(script);
  };

  const handleLogout = () => {
    openLink(router, { href: '/open-logout' });
  };

  return (
    <div className="container mx-auto my-3 lg:pt-2">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-10 left-1/2 z-50 -translate-x-1/2 transform transition-opacity duration-500">
          <div
            className="rounded bg-black px-6 py-4 text-white shadow-lg"
            style={{ height: '70px', maxWidth: '100%', marginTop: '-35px' }}
          >
            <div className="flex h-full items-center justify-center">
              <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Thank you for submitting your Marketing Email Preferences for Stylus. Our records have been updated.
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="w-full">
          <div className="cms-section rounded bg-white shadow-none">
            <div>
              <h1 className="mb-8 text-3xl font-normal text-gray-900">Profile</h1>

              {/* User Information */}
              <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2">
                <div>
                  <h2 className="mb-2 text-lg font-medium text-gray-900">Name</h2>
                  <div className="border-b border-gray-300 pb-2">
                    <p className="text-gray-400">{fullName}</p>
                  </div>
                </div>
                <div>
                  <h2 className="mb-2 text-lg font-medium text-gray-900">Job Title</h2>
                  <div className="border-b border-gray-300 pb-2">
                    <p className="text-gray-400">{jobTitle}</p>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="mb-2 text-lg font-medium text-gray-900">Your email</h2>
                <div className="border-b border-gray-300 pb-2">
                  <p className="text-gray-400">{email}</p>
                </div>
              </div>

              {/* Logout Button */}
              <div className="mb-8">
                <button className="btn secondary" onClick={handleLogout} type="button">
                  Logout
                </button>
              </div>

              {/* HubSpot Form */}
              <div id="hubspot-form" className="px-3"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModel;
